import { config } from "../../config";
import { normalizePointer } from "./pointer";

/**
 * Ramène un écart d'angle dans [-π, π]. Les deux doigts peuvent passer d'un
 * côté à l'autre de ±π entre deux images : sans ce repli, la caméra ferait un
 * tour complet sur un frémissement.
 */
const wrapAngle = (angle: number): number => {
  const turn = Math.PI * 2;
  return ((((angle + Math.PI) % turn) + turn) % turn) - Math.PI;
};
import type { InputSample, InputSource } from "../../types/input";

export interface TouchOptions {
  /** Zone de visée : le canvas, où tous les gestes sont lus. */
  lookTarget: HTMLElement;
}

interface Point {
  x: number;
  y: number;
}

/**
 * Source tactile. Elle branche ses écouteurs sur des éléments fournis par
 * l'UI : celle-ci dessine et ne lit jamais un événement,
 * toute la logique d'entrée reste ici.
 */
export class Touch implements InputSource {
  readonly name = "touch";

  private readonly listeners = new AbortController();
  private readonly lookPointers = new Map<number, Point>();
  private readonly lookDelta: Point = { x: 0, y: 0 };
  private zoomDelta = 0;
  private pinchDistance: number | null = null;
  /** Position normalisée du dernier appui. Le survol n'existe pas ici ([GD-007]). */
  private readonly pointer = { x: 0, y: 0 };
  /** Milieu des deux doigts, tant qu'ils pincent. Nul le reste du temps. */
  private readonly pinchCenter = { x: 0, y: 0 };
  private pinching = false;
  private pick = false;
  private tapStart: { x: number; y: number; time: number } | null = null;
  private tapTravelled = 0;

  /** Glissement d'un doigt, en pixels, vidé à chaque image ([GD-045]). */
  private readonly moveDelta: Point = { x: 0, y: 0 };
  /** Milieu des deux doigts en pixels, pour en mesurer la montée. */
  private pinchPixels: Point | null = null;
  /** Angle de la droite qui joint les deux doigts, pour en mesurer la rotation. */
  private pinchAngleValue: number | null = null;

  constructor(options: TouchOptions) {
    const { signal } = this.listeners;
    const target = options.lookTarget;

    target.addEventListener("pointerdown", this.onLookDown, { signal });
    target.addEventListener("pointermove", this.onLookMove, { signal });
    target.addEventListener("pointerup", this.onLookUp, { signal });
    target.addEventListener("pointercancel", this.onLookUp, { signal });
  }

  /** `dt` n'est pas utilisé : les gestes tactiles sont continus, rien à étaler. */
  drain(out: InputSample, _dt: number): void {
    out.moveDelta.x += this.moveDelta.x;
    out.moveDelta.y += this.moveDelta.y;
    out.lookDelta.x += this.lookDelta.x;
    out.lookDelta.y += this.lookDelta.y;
    out.zoomDelta += this.zoomDelta;

    if (this.pick) {
      out.pointer.x = this.pointer.x;
      out.pointer.y = this.pointer.y;
      out.pick = true;
      this.pick = false;
    } else if (this.pinching) {
      // Le pincement a une position sans rien survoler : le zoom y converge,
      // mais `pointerPresent` reste faux et rien ne se désigne ([GD-043]).
      out.pointer.x = this.pinchCenter.x;
      out.pointer.y = this.pinchCenter.y;
      out.zoomAnchored = true;
    }
    // `pointerPresent` reste faux : un doigt n'a pas de position hors contact.

    this.lookDelta.x = 0;
    this.lookDelta.y = 0;
    this.zoomDelta = 0;
    this.moveDelta.x = 0;
    this.moveDelta.y = 0;
  }

  private readonly onLookDown = (event: PointerEvent): void => {
    if (event.pointerType === "mouse") return;
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
    this.lookPointers.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });
    this.pinchDistance = null;
    // Un second doigt annule l'appui en cours : c'est un pincement, pas un tap.
    this.tapStart =
      this.lookPointers.size === 1
        ? { x: event.clientX, y: event.clientY, time: performance.now() }
        : null;
    this.tapTravelled = 0;
  };

  private readonly onLookMove = (event: PointerEvent): void => {
    const previous = this.lookPointers.get(event.pointerId);
    if (!previous) return;

    // Un doigt déplace le terrain, qui suit le doigt ([GD-045]). L'axe Z
    // pointant vers l'observateur, seul X change de signe.
    if (this.lookPointers.size === 1) {
      this.moveDelta.x -= event.clientX - previous.x;
      this.moveDelta.y += event.clientY - previous.y;
      this.tapTravelled +=
        Math.abs(event.clientX - previous.x) +
        Math.abs(event.clientY - previous.y);
    }
    previous.x = event.clientX;
    previous.y = event.clientY;

    // Deux doigts font trois choses à la fois, chacune sur une composante
    // différente du geste ([GD-045]) : leur écartement zoome, l'angle qui les
    // joint fait tourner, et leur milieu monté ou descendu incline.
    if (this.lookPointers.size === 2) {
      const distance = this.pinchSpan();
      if (this.pinchDistance !== null) {
        this.zoomDelta +=
          (distance - this.pinchDistance) * config.input.pinchZoomGain;
      }
      this.pinchDistance = distance;

      const angle = this.pinchAngle();
      if (angle !== null && this.pinchAngleValue !== null) {
        // L'écran a son Y vers le bas : une rotation horaire des doigts fait
        // croître l'angle, et le monde doit tourner dans le même sens.
        this.lookDelta.x -=
          wrapAngle(angle - this.pinchAngleValue) *
          config.input.pinchTurnPixels;
      }
      this.pinchAngleValue = angle;

      const center = this.pinchMidpoint();
      if (center && this.pinchPixels)
        this.lookDelta.y -= center.y - this.pinchPixels.y;
      this.pinchPixels = center;
      this.pinching = this.readPinchCenter(event.target as HTMLElement);
    }
  };

  private readonly onLookUp = (event: PointerEvent): void => {
    // Un appui bref et immobile désigne ; au-delà, c'était un balayage.
    if (
      this.tapStart &&
      this.lookPointers.size === 1 &&
      this.tapTravelled <= config.input.touchTapMaxDistance &&
      performance.now() - this.tapStart.time <= config.input.tapMaxDuration
    ) {
      this.pick = normalizePointer(
        event,
        event.target as HTMLElement,
        this.pointer,
      );
    }
    this.tapStart = null;
    this.lookPointers.delete(event.pointerId);
    this.pinchDistance = null;
    this.pinchPixels = null;
    this.pinchAngleValue = null;
    this.pinching = false;
  };

  /** Angle de la droite joignant les deux doigts, en radians. */
  private pinchAngle(): number | null {
    const [first, second] = [...this.lookPointers.values()];
    if (!first || !second) return null;
    return Math.atan2(second.y - first.y, second.x - first.x);
  }

  /** Milieu des deux doigts, en pixels. */
  private pinchMidpoint(): Point | null {
    const [first, second] = [...this.lookPointers.values()];
    if (!first || !second) return null;
    return { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
  }

  /** Milieu des deux doigts, en coordonnées normalisées. Faux si on ne peut pas. */
  private readPinchCenter(target: HTMLElement): boolean {
    const [first, second] = [...this.lookPointers.values()];
    if (!first || !second) return false;
    return normalizePointer(
      { clientX: (first.x + second.x) / 2, clientY: (first.y + second.y) / 2 },
      target,
      this.pinchCenter,
    );
  }

  private pinchSpan(): number {
    const [first, second] = [...this.lookPointers.values()];
    if (!first || !second) return 0;
    return Math.hypot(second.x - first.x, second.y - first.y);
  }

  dispose(): void {
    this.listeners.abort();
    this.lookPointers.clear();
    // Une source libérée doit être inerte : sans ça, un geste interrompu
    // pendant la destruction continuerait d'imposer sa dernière valeur.
    this.moveDelta.x = 0;
    this.moveDelta.y = 0;
    this.pinchPixels = null;
    this.pinchAngleValue = null;
    this.lookDelta.x = 0;
    this.lookDelta.y = 0;
    this.zoomDelta = 0;
    this.pick = false;
    this.pinching = false;
    this.tapStart = null;
  }
}
